import axios from 'axios';
import { ActionTree } from 'vuex';
import {
  mutationInitalizeDocumentEdit,
  mutationLoadDocumentData,
  mutationLoadingRecipients,
  mutationRemoveVideo,
  mutationSetDocumentData,
  mutationSetIsSelfsign,
  mutationSetIsSigning,
  mutationSetIsSigningOrder,
  mutationSetNewType,
  mutationSetRecipients,
  mutationSetSender,
  mutationSetVideo,
  mutationSetDocumentId,
  mutationSetActiveTab,
  mutationSetError,
} from '../mutations/document-edit.mutations';
import { RootState } from '../store';
import { DocumentEditState } from '../stores/document-edit.store';
import { ISender } from '@/types/Sender';
import { IRecipient } from '@/types/Recipient';
import { IDocument } from '@/types/Document';
import { EnumDocumentType } from '@/enums/DocumentType';
import { DocumentService } from '@/api/documents/services/document.service';
import { translate } from '@/helpers/translation.helper';
import { ISession } from '@/types/Session';
import { VideoService } from '@/api/video/services/video.service';
import { IUser } from '@/types/User';
import { getIntegrationTemplate } from '@/helpers/signup-template.helper';
import bugsnagClient from '@lib-shared/bugsnag';

export const actionSetRecipients = 'setRecipients';
export const actionSetIsSigning = 'setIsSigning';
export const actionSetIsSigningOrder = 'setIsSigningOrder';
export const actionLoadDocumentData = 'loadLegacyDocument';
export const actionSetIsSelfsign = 'setIsSelfsign';
export const actionSetRecipientsSigningOrders = 'setRecipientsSigningOrder';
export const actionSetRecipientSigningOrder = 'setRecipientSigningOrder';
export const actionRemoveVideo = 'removeVideo';
export const actionSetVideoToDocument = 'setVideoToDocument';
export const actionInitializeDocumentEdit = 'initializeDocumentEdit';
export const actionSetNewType = 'setNewType';
export const actionCreateDocument = 'createDocument';
export const actionCreateDocumentSuccess = 'createDocumentSuccess';
export const actionCreateDocumentFailed = 'createDocumentFailed';
export const actionSetActiveTab = 'setActiveTab';
export const actionResetError = 'resetError';

export const actions: ActionTree<DocumentEditState, RootState> = {
  async [actionInitializeDocumentEdit]({ commit }) {
    commit(mutationInitalizeDocumentEdit);
  },
  async [actionSetRecipients]({ commit }, recipients: IRecipient[]) {
    commit(mutationSetRecipients, recipients);
  },
  async [actionSetIsSigning]({ commit }, { documentId, isSigning }) {
    commit(mutationSetIsSigning, isSigning);
    const body = new FormData();
    body.append('action', 'data');
    body.append('document-id', documentId);
    body.append('field', 'document-is_signing');
    body.append('value', isSigning ? '1' : '0');
    const {
      data: { status },
    } = await axios.post(`/save/document`, body);
    if (status !== 1) {
      commit(mutationSetIsSigning, !isSigning);
    }
  },
  async [actionLoadDocumentData]({ commit }, documentId) {
    commit(mutationLoadDocumentData, true);
    const {
      data: { document },
    } = await axios.get(`/get/document/${documentId}/details`);

    if (document) {
      const documentData: IDocument = {
        name: document.name,
        sender: document.sender,
        documentId: document.id,
        isSigning: document.is_signing,
        isSigningOrder: document.is_signing_order,
        isSelfsign: document.is_selfsign,
        type: document.type,
        videoId: document.video_id,
        isVideo: document.is_video,
      };

      const senderData: ISender = {
        email: document.sender_email,
        name: document.sender_name,
        thumbUrl: document.sender_thumb_url,
        senderId: document.user_id,
      };
      commit(mutationSetDocumentData, documentData);
      commit(mutationSetSender, senderData);
    }
    commit(mutationLoadDocumentData, false);
  },
  async [actionSetNewType]({ commit }, newType: EnumDocumentType) {
    commit(mutationSetNewType, newType);
  },
  async [actionSetIsSigningOrder]({ commit, state }, { documentId, isSigningOrder }) {
    if (state.isSigningOrder === isSigningOrder) {
      return;
    }
    commit(mutationSetIsSigningOrder, isSigningOrder);
    const body = new FormData();
    body.append('action', 'data');
    body.append('document-id', documentId);
    body.append('field', 'document-is_signing_order');
    body.append('value', isSigningOrder ? '1' : '0');
    const {
      data: { status },
    } = await axios.post(`/save/document`, body);
    if (status !== 1) {
      commit(mutationSetIsSigningOrder, !isSigningOrder);
    }
  },
  async [actionSetIsSelfsign]({ commit }, { documentId, isSelfsign }) {
    commit(mutationSetIsSelfsign, isSelfsign);
    const body = new FormData();
    body.append('action', 'data');
    body.append('document-id', documentId);
    body.append('field', 'document-is_selfsign');
    body.append('value', isSelfsign ? '1' : '0');
    const {
      data: { status },
    } = await axios.post(`/save/document`, body);
    if (status !== 1) {
      commit(mutationSetIsSelfsign, !isSelfsign);
    }
  },
  async [actionSetRecipientsSigningOrders]({ commit }, { documentId, orderData }) {
    commit(mutationLoadingRecipients, true);
    const body = new FormData();
    body.append('action', 'order');
    body.append('document-id', documentId);

    orderData.forEach(data => {
      body.append('order-data[]', data);
    });

    await axios.post('/save/recipient', body);
    commit(mutationLoadingRecipients, false);
  },
  async [actionSetRecipientSigningOrder]({ commit }, { documentId, recipientId, orderNum }) {
    commit(mutationLoadingRecipients, true);

    const body = new FormData();
    body.append('action', 'order_num');
    body.append('document-id', documentId);
    body.append('recipient-id', recipientId);
    body.append('recipient-order_num', orderNum);
    await axios.post('/save/recipient', body);
    commit(mutationLoadingRecipients, false);
  },

  async [actionRemoveVideo](
    { commit },
    { videoId, documentId }: { videoId: string; documentId: string }
  ) {
    await VideoService.removeEngageVideo(videoId, documentId);
    commit(mutationRemoveVideo);
  },

  async [actionSetVideoToDocument]({ commit }, videoId: string) {
    commit(mutationSetVideo, videoId);
  },
  async [actionCreateDocument](
    { dispatch, commit },
    { session, user }: { session: ISession; user: IUser }
  ) {
    try {
      commit(mutationSetError, false);
      const templateId: string = getIntegrationTemplate(user.registrationCampaign);
      const documentId: string = await DocumentService.createSampleDocument(session, templateId);
      dispatch(actionCreateDocumentSuccess, documentId);
    } catch (e) {
      dispatch(actionCreateDocumentFailed, e);
    }
  },
  async [actionCreateDocumentSuccess]({ commit }, documentId: string) {
    commit(mutationSetDocumentId, documentId);
  },
  async [actionCreateDocumentFailed]({ dispatch, commit }, e: Error) {
    if (bugsnagClient) {
      bugsnagClient.notify(e);
    }
    commit(mutationSetError, true);
    dispatch('alert/showDangerAlert', translate('could-not-create-document-from-template'), {
      root: true,
    });
  },
  async [actionSetActiveTab]({ commit }, tab: string) {
    commit(mutationSetActiveTab, tab);
  },
  [actionResetError]({ commit }) {
    commit(mutationSetError, false);
  },
};
